import { doc, getDoc, setDoc, Timestamp } from "firebase/firestore";
import { db } from "../utils/firebaseConfig";
import { Despesa, DespesaFirestore, ModoDivisao } from "../types/app";
import { logger } from '../utils/logger';
import { validateDespesa, validateValorMonetario, sanitizeString } from '../utils/validation';
import {
  handleError,
  createSuccessResponse,
  withErrorHandling,
  requireAuth,
  requireResource
} from '../utils/errorHandler';

const requireDb = () => {
  if (!db) {
    throw new Error('Firebase não configurado neste build.');
  }
};

// Função auxiliar para validar e converter datas
const validateAndConvertDate = (date: any): Date => {
  try {
    if (date instanceof Date) {
      return date;
    }
    if (date instanceof Timestamp) {
      return date.toDate();
    }
    if (typeof date === 'string') {
      const parsed = new Date(date);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }
    if (date && typeof date === 'object') {
      if ('seconds' in date) {
        const parsed = new Date((date as any).seconds * 1000);
        if (!isNaN(parsed.getTime())) {
          return parsed;
        }
      }
      if ('toDate' in date && typeof date.toDate === 'function') {
        return date.toDate();
      }
    }
    logger.warn('Invalid date detected, using current date');
    return new Date();
  } catch (error) {
    logger.warn('Error processing date', { error });
    return new Date();
  }
};

// Remover coleção global de despesas

export const criarDespesa = withErrorHandling(async (
  rachaId: string,
  despesa: Omit<Despesa, 'id'> & { id?: string }
): Promise<Despesa> => {
  requireDb();
  // Validação do ID do racha
  const sanitizedRachaId = sanitizeString(rachaId);
  requireResource(sanitizedRachaId, 'ID do racha');

  // Validação dos dados da despesa
  const validation = validateDespesa(despesa);
  if (!validation.isValid) {
    logger.warn('Invalid expense data provided', { errors: validation.errors, despesa });
    throw new Error(`Dados da despesa inválidos: ${validation.errors.join(', ')}`);
  }

  const sanitizedDespesa = validation.sanitizedValue;

  // Validar data usando a função auxiliar
  const dataValida = validateAndConvertDate(sanitizedDespesa.data);

  const id = sanitizedDespesa.id || String(Date.now());
  const novaDespesa: Despesa = {
    id,
    titulo: sanitizedDespesa.titulo,
    valor: sanitizedDespesa.valor,
    data: dataValida,
    categoria: sanitizedDespesa.categoria,
    pagoPorId: sanitizedDespesa.pagoPorId,
    pagoPorNome: sanitizedDespesa.pagoPorNome,
    modoDivisao: sanitizedDespesa.modoDivisao || 'igual',
    divisoes: sanitizedDespesa.divisoes || []
  };

  // Buscar o documento principal do racha
  const rachaRef = doc(db, 'rachas', sanitizedRachaId);
  const rachaSnap = await getDoc(rachaRef);

  requireResource(rachaSnap.exists(), 'Racha');

  const rachaData = rachaSnap.data();
  const despesas = rachaData.despesas || [];

  // Adicionar a nova despesa ao array
  const despesasAtualizadas = [...despesas, novaDespesa];

  // Salvar o documento principal atualizado
  await setDoc(rachaRef, {
    ...rachaData,
    despesas: despesasAtualizadas,
    dataAtualizacao: new Date()
  }, { merge: true });

  return novaDespesa;
}, 'criarDespesa');

export const excluirDespesa = async (rachaId: string, despesaId: string): Promise<void> => {
  try {
    requireDb();
    // Buscar o documento principal do racha
    const rachaRef = doc(db, 'rachas', rachaId);
    const rachaSnap = await getDoc(rachaRef);

    if (!rachaSnap.exists()) {
      throw new Error('Racha não encontrado');
    }

    const rachaData = rachaSnap.data();
    const despesas = rachaData.despesas || [];

    // Filtrar a despesa a ser removida
    const despesasAtualizadas = despesas.filter((d: any) => d.id !== despesaId);

    // Salvar o documento principal atualizado
    await setDoc(rachaRef, {
      ...rachaData,
      despesas: despesasAtualizadas,
      dataAtualizacao: new Date()
    }, { merge: true });
  } catch (error) {
    logger.error('Error deleting expense', { error, rachaId, despesaId });
    // Adicionar mais contexto ao erro
    if (error instanceof Error) {
      throw new Error(`Falha ao excluir despesa: ${error.message}`);
    }
    throw new Error('Falha ao excluir despesa: erro desconhecido');
  }
}
export const obterDespesa = async (rachaId: string, despesaId: string): Promise<Despesa | null> => {
  try {
    requireDb();
    // Buscar o documento principal do racha
    const rachaRef = doc(db, 'rachas', rachaId);
    const rachaSnap = await getDoc(rachaRef);

    if (!rachaSnap.exists()) {
      return null;
    }

    const rachaData = rachaSnap.data();
    const despesas = rachaData.despesas || [];

    // Encontrar a despesa no array
    const despesa = despesas.find((d: any) => d.id === despesaId);
    if (!despesa) {
      return null;
    }

    // Validar e converter data
    const dataValida = validateAndConvertDate(despesa.data);

    return {
      ...despesa,
      id: despesaId,
      data: dataValida,
    } as Despesa;
  } catch (error) {
    logger.error('Error getting expense', { error, rachaId, despesaId });
    throw error;
  }
}

export const obterDespesasPorRacha = async (rachaId: string): Promise<Despesa[]> => {
  try {
    requireDb();
    // Buscar o documento principal do racha
    const rachaRef = doc(db, 'rachas', rachaId);
    const rachaSnap = await getDoc(rachaRef);

    if (!rachaSnap.exists()) {
      return [];
    }

    const rachaData = rachaSnap.data();
    const despesas = rachaData.despesas || [];

    // Mapear e validar as despesas
    return despesas.map((despesa: any) => {
      const dataValida = validateAndConvertDate(despesa.data);
      return {
        ...despesa,
        data: dataValida,
      } as Despesa;
    });
  } catch (error) {
    logger.error('Error getting expenses for racha', { error, rachaId });
    throw error;
  }
}

export const atualizarDespesa = async (
  rachaId: string,
  despesaId: string,
  updates: Partial<Omit<Despesa, 'id'>>
): Promise<Despesa> => {
  try {
    requireDb();
    // Validação do ID do racha
    const sanitizedRachaId = sanitizeString(rachaId);
    if (!sanitizedRachaId) {
      throw new Error('ID do racha inválido');
    }

    // Validação dos dados de atualização (se houver campos obrigatórios)
    if (updates.titulo !== undefined || updates.valor !== undefined) {
      const partialDespesa = {
        titulo: updates.titulo || 'temp',
        valor: updates.valor || 0,
        data: updates.data || new Date(),
        pagoPorId: updates.pagoPorId || 'temp',
        pagoPorNome: updates.pagoPorNome || 'temp',
        modoDivisao: updates.modoDivisao || 'igual',
        divisoes: updates.divisoes || []
      };
      const validation = validateDespesa(partialDespesa);
      if (!validation.isValid) {
        logger.warn('Invalid expense update data', { errors: validation.errors });
        throw new Error(`Dados de atualização inválidos: ${validation.errors.join(', ')}`);
      }
    }

    // Buscar o documento principal do racha
    const rachaRef = doc(db, 'rachas', sanitizedRachaId);
    const rachaSnap = await getDoc(rachaRef);

    if (!rachaSnap.exists()) {
      throw new Error('Racha não encontrado');
    }

    const rachaData = rachaSnap.data();
    const despesas = rachaData.despesas || [];

    // Encontrar a despesa no array
    const despesaIndex = despesas.findIndex((d: any) => d.id === despesaId);
    if (despesaIndex === -1) {
      throw new Error('Despesa não encontrada');
    }

    // Preparar updates limpos (sem campos undefined)
    const updatesLimpos: any = {};
    Object.keys(updates).forEach(key => {
      const value = (updates as any)[key];
      if (value !== undefined) {
        if (key === 'data') {
          // Validar e converter data
          try {
            let dataValida: Date;
            if (value instanceof Date) {
              dataValida = value;
            } else if (typeof value === 'string') {
              dataValida = new Date(value);
            } else {
              dataValida = new Date();
            }
            // Verificar se a data é válida
            if (isNaN(dataValida.getTime())) {
              logger.warn('Invalid date detected, keeping current date');
              // Não incluir data inválida
            } else {
              updatesLimpos[key] = dataValida;
            }
          } catch (error) {
            logger.warn('Error processing update date', { error });
            // Não incluir data com erro
          }
        } else {
          updatesLimpos[key] = value;
        }
      }
    });

    // Atualizar a despesa no array
    const despesaAtualizada = {
      ...despesas[despesaIndex],
      ...updatesLimpos,
      id: despesaId,
    } as Despesa;

    // Atualizar o array de despesas
    const despesasAtualizadas = [...despesas];
    despesasAtualizadas[despesaIndex] = despesaAtualizada;

    // Salvar o documento principal atualizado
    await setDoc(rachaRef, {
      ...rachaData,
      despesas: despesasAtualizadas,
      dataAtualizacao: new Date()
    }, { merge: true });

    return despesaAtualizada;
  } catch (error) {
    logger.error('Error updating expense', { error, rachaId, despesaId, updates });
    throw error;
  }
}
